import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconMessages } from '@components/icons/messages';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRemove } from '@components/icons/remove';
import { IconTrash } from '@components/icons/trash';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { theme } from '@styles/theme';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  useDeleteCompletionTemplate,
  useGetCompletionTemplates,
  useNotifyEmailsList,
  useRemoveNotifyRecipient,
} from './api/hooks';
import {
  CompletionTemplate,
  CompletionTemplateTypes,
  onboardingFormGetSms,
  onboardingFormSetSms,
  Recipient,
  SmsDataResponse,
  SubmitError,
} from './api/requests';

export const OnboardingFormSettingsPage = () => {
  const { t } = useStore();

  return (
    <>
      <Stack space={6}>
        <Text font="heading2" text={t('integrations.onboardingform.settings.header')} />
        <Separator />
        <SmsSection />
        <Separator />
        <CompletionEmailsSections />
      </Stack>
    </>
  );
};

const SmsSection = () => {
  const { showError } = useToaster();
  const smsDataQuery = useQuery({
    queryKey: ['smsData'],
    queryFn: onboardingFormGetSms,
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });

  if (smsDataQuery.isLoading) {
    return <RelativeSpinner />;
  }

  if (smsDataQuery.data === undefined) {
    return null;
  }

  return <SmsMessage smsData={smsDataQuery.data} />;
};

const SmsMessage = (props: { smsData: SmsDataResponse }) => {
  const { smsData } = props;
  const { t } = useStore();
  const { showModal } = useModal();
  const { showError, showSuccess } = useToaster();

  const [enabled, setEnabled] = useState<Boolean>(smsData.sendSms);

  const queryClient = useQueryClient();

  const disable = useMutation(() => onboardingFormSetSms('', false), {
    onSuccess: () => {
      setEnabled(false);
      queryClient.invalidateQueries(['onboarding-form-data']);
      showSuccess(t('integrations.onboardingform.settings.sms.disable.toast'));
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });

  const openModal = (onEdit: boolean = false, smsTemplate: string) => {
    showModal('INTEGRATION_ONBOARDING_FORM_SMS_MESSAGE', {
      _container: {
        width: '600px',
      },
      setEnabled,
      onEdit,
      template: smsTemplate,
    });
  };

  return (
    <Stack space={4}>
      <Stack space={2}>
        <Stack orientation="horizontal" space={2} align="center">
          <Text font="bold18" text={t('integrations.onboardingform.settings.sms')} />
          <Tag
            variant="purple"
            size="small"
            text={t('integrations.onboardingform.settings.sms.tag')}
          />
        </Stack>
        <Text text={t('integrations.onboardingform.settings.sms.instructions')} />
      </Stack>
      <Card variant="default" spaceX={6} spaceY={4} interactive>
        <Stack
          orientation="horizontal"
          space={4}
          justify="space-between"
          style={{ minHeight: theme.base.height }}
        >
          <Stack orientation="horizontal" space={4} align="center">
            <IconMessages />
            <Stack space={0}>
              <Text font="semibold14" text={t('integrations.onboardingform.settings.sms')} />
              {enabled ? (
                <Text font="regular14" text={t('integrations.onboardingform.settings.enabled')} />
              ) : (
                <Text font="regular14" text={t('integrations.onboardingform.settings.disabled')} />
              )}
            </Stack>
          </Stack>
          {enabled ? (
            <Menu
              buttonProps={{
                variant: 'ghost',
                iconLeft: <IconMoreVertical />,
              }}
            >
              <Menu.Item
                icon={<IconEdit />}
                variant="base"
                label={t('integrations.onboardingform.settings.sms.menu.edit')}
                onSelect={() => openModal(true, smsData.smsTemplate)}
              />
              <Menu.Item
                icon={<IconRemove />}
                variant="base"
                label={t('integrations.onboardingform.settings.sms.menu.disable')}
                onSelect={() => disable.mutate()}
              />
            </Menu>
          ) : (
            <Button
              variant="ghost"
              text={t('integrations.onboardingform.settings.enable')}
              onClick={() => openModal(false, smsData.smsTemplate)}
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

const CompletionEmailsSections = () => {
  const templatesQuery = useGetCompletionTemplates();

  if (templatesQuery.isLoading) {
    return <RelativeSpinner />;
  }

  if (templatesQuery.error) {
    return null;
  }

  return (
    <>
      {templatesQuery.data && (
        <>
          <NotifyEmails template={templatesQuery.data.notifyTemplate} />
          <Separator />
          <ApplicantEmail template={templatesQuery.data.applicantTemplate} />
        </>
      )}
    </>
  );
};

const NotifyEmails = (props: { template: CompletionTemplate | null }) => {
  const { template } = props;
  const { t } = useStore();
  const { showModal } = useModal();
  const [emailEnabled, setEmailEnabled] = useState(template ? true : false);

  const notifyEmailsQuery = useNotifyEmailsList();
  const remove = useRemoveNotifyRecipient();

  function openRecipientModal(recipient?: Recipient) {
    showModal('INTEGRATION_ONBOARDING_FORM_NOTIFY_EMAIL', {
      recipient,
    });
  }

  const openTemplateModal = (
    template: CompletionTemplate | null,
    template_type: CompletionTemplateTypes
  ) => {
    showModal('INTEGRATION_ONBOARDING_FORM_COMPLETION_TEMPLATES', {
      _container: {
        width: '700px',
      },
      setEmailEnabled,
      template,
      template_type,
    });
  };

  const onDisableSuccess = () => {
    setEmailEnabled(false);
    if (notifyEmailsQuery.data?.length !== 0) {
      notifyEmailsQuery.data?.forEach((recipient) => {
        remove.mutate(recipient.id);
      });
    }
  };
  const disableTemplate = useDeleteCompletionTemplate(onDisableSuccess);

  const disableEmail = () => {
    if (template) {
      // @ts-expect-error
      showModal('CONFIRM_MODAL', {
        confirm: () => {
          disableTemplate.mutate(template.id);
          return true;
        },
        data: {
          id: template.id,
        },
        buttonIcon: <IconRemove />,
        headerText: t('integrations.onboardingform.settings.email.disable.modal.header'),
        mainText: t('integrations.onboardingform.settings.email.disable.modal.body'),
        confirmText: t('integrations.onboardingform.settings.email.disable.modal.disable'),
      });
    }
  };

  return (
    <Stack space={4}>
      <Stack space={2}>
        <Text font="bold18" text={t('integrations.onboardingform.settings.email')} />
        <Text text={t('integrations.onboardingform.settings.email.instructions')} />
      </Stack>
      {notifyEmailsQuery.isLoading ? (
        <RelativeSpinner />
      ) : (
        <>
          <Stack space={4}>
            <Card variant="default" spaceX={6} spaceY={4} interactive>
              <Stack
                orientation="horizontal"
                space={4}
                justify="space-between"
                style={{ minHeight: theme.base.height }}
              >
                <Stack orientation="horizontal" space={4} align="center">
                  <IconEmail />
                  <Stack space={0}>
                    <Text
                      font="semibold14"
                      text={t('integrations.onboardingform.settings.email.card.header')}
                    />
                    {emailEnabled ? (
                      <Text
                        font="regular14"
                        text={t('integrations.onboardingform.settings.enabled')}
                      />
                    ) : (
                      <Text
                        font="regular14"
                        text={t('integrations.onboardingform.settings.disabled')}
                      />
                    )}
                  </Stack>
                </Stack>
                {emailEnabled ? (
                  <Menu
                    buttonProps={{
                      variant: 'ghost',
                      iconLeft: <IconMoreVertical />,
                    }}
                  >
                    <Menu.Item
                      icon={<IconEdit />}
                      variant="base"
                      label={t('integrations.onboardingform.settings.email.menu.edit')}
                      onSelect={() =>
                        openTemplateModal(template, 'integrations_rookies_completion_notify')
                      }
                    />
                    <Menu.Item
                      icon={<IconRemove />}
                      variant="base"
                      label={t('integrations.onboardingform.settings.email.menu.disable')}
                      onSelect={disableEmail}
                    />
                  </Menu>
                ) : (
                  <Button
                    variant="ghost"
                    text={t('integrations.onboardingform.settings.enable')}
                    onClick={() =>
                      openTemplateModal(template, 'integrations_rookies_completion_notify')
                    }
                  />
                )}
              </Stack>
            </Card>
            {notifyEmailsQuery.data?.length !== 0 && (
              <Stack space={2}>
                <Text
                  font="semibold14"
                  text={t('integrations.onboardingform.settings.email.recipients')}
                />
                {notifyEmailsQuery.data?.map((recipient, idx) => (
                  <Card variant="default" spaceX={6} spaceY={4} interactive key={idx}>
                    <Stack orientation="horizontal" space={4} justify="space-between">
                      <Stack space={0}>
                        <Text font="semibold14" text={recipient.name} />
                        <Text font="regular14" color="purple80" text={recipient.email} />
                      </Stack>
                      <Menu
                        buttonProps={{
                          variant: 'ghost',
                          iconLeft: <IconMoreVertical />,
                        }}
                      >
                        <Menu.Item
                          icon={<IconEdit />}
                          variant="base"
                          label={t('integrations.onboardingform.settings.email.edit')}
                          onSelect={() => openRecipientModal(recipient)}
                        />
                        <Menu.Item
                          icon={<IconTrash />}
                          variant="base"
                          label={t('integrations.onboardingform.settings.email.remove')}
                          onSelect={() => remove.mutate(recipient.id)}
                        />
                      </Menu>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
          {emailEnabled && (
            <Stack orientation="horizontal" space={0}>
              <Button
                variant="secondary"
                iconLeft={<IconAddPlus />}
                text={t('integrations.onboardingform.settings.submit')}
                onClick={() => openRecipientModal()}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

const ApplicantEmail = (props: { template: CompletionTemplate | null }) => {
  const { template } = props;
  const { t } = useStore();
  const { showModal } = useModal();
  const [emailEnabled, setEmailEnabled] = useState(template ? true : false);

  const openModal = (
    template: CompletionTemplate | null,
    template_type: CompletionTemplateTypes
  ) => {
    showModal('INTEGRATION_ONBOARDING_FORM_COMPLETION_TEMPLATES', {
      _container: {
        width: '700px',
      },
      setEmailEnabled,
      template,
      template_type,
    });
  };

  const onDisableSuccess = () => {
    setEmailEnabled(false);
  };
  const disableTemplate = useDeleteCompletionTemplate(onDisableSuccess);

  function disableEmail() {
    if (template) {
      disableTemplate.mutate(template.id);
    }
  }

  return (
    <Stack space={4}>
      <Stack space={2}>
        <Text font="bold18" text={t('integrations.onboardingform.settings.email.applicant')} />
        <Text text={t('integrations.onboardingform.settings.email.applicant.instructions')} />
      </Stack>
      <Card variant="default" spaceX={6} spaceY={4} interactive>
        <Stack
          orientation="horizontal"
          space={4}
          justify="space-between"
          style={{ minHeight: theme.base.height }}
        >
          <Stack orientation="horizontal" space={4} align="center">
            <IconEmail />
            <Stack space={0}>
              <Text
                font="semibold14"
                text={t('integrations.onboardingform.settings.email.card.header')}
              />
              {emailEnabled ? (
                <Text font="regular14" text={t('integrations.onboardingform.settings.enabled')} />
              ) : (
                <Text font="regular14" text={t('integrations.onboardingform.settings.disabled')} />
              )}
            </Stack>
          </Stack>
          {emailEnabled ? (
            <Menu
              buttonProps={{
                variant: 'ghost',
                iconLeft: <IconMoreVertical />,
              }}
            >
              <Menu.Item
                icon={<IconEdit />}
                variant="base"
                label={t('integrations.onboardingform.settings.email.menu.edit')}
                onSelect={() => openModal(template, 'integrations_rookies_completion_applicant')}
              />
              <Menu.Item
                icon={<IconRemove />}
                variant="base"
                label={t('integrations.onboardingform.settings.email.menu.disable')}
                onSelect={() => disableEmail()}
              />
            </Menu>
          ) : (
            <Button
              variant="ghost"
              text={t('integrations.onboardingform.settings.enable')}
              onClick={() => openModal(template, 'integrations_rookies_completion_applicant')}
            />
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
